// My Tasks sections, the pure half (WOS-BUNDLE; Work OS `myTasks` / `setMyTaskBucket`, the
// `workos-mytasks-group` preference): which section each task is in (a task without a placement
// is in Recently assigned), the order inside a section, where a drop lands on the server, the
// optimistic copy of a move, and the person's group-by choice (Due date or Sections) in this
// browser. Sections is the default, so the feature is seen.
import type { MySections } from "./bundle-queries";

export type GroupBy = "due" | "sections";
const GROUP_KEY = "companyos-mytasks-group";

function store(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function readGroupBy(): GroupBy {
  try {
    return store()?.getItem(GROUP_KEY) === "due" ? "due" : "sections";
  } catch {
    return "sections";
  }
}

export function writeGroupBy(v: GroupBy) {
  try {
    store()?.setItem(GROUP_KEY, v);
  } catch {}
}

type Placement = MySections["placements"][number];
type Task = { id: string; due_at: string | null; number: number };

/**
 * Each section with its tasks: placed ones by their position, then (in Recently assigned) the
 * tasks without a placement, by due date. A placement in a section that no longer exists counts
 * as none.
 */
export function arrangeSections<T extends Task>(
  data: MySections,
  tasks: readonly T[],
): { id: string; name: string; builtin: boolean; tasks: T[] }[] {
  const known = new Set(data.sections.map((s) => s.id));
  const where = new Map(
    data.placements.filter((p) => known.has(p.section)).map((p) => [p.task_id, p] as const),
  );
  const ordered = [...data.sections].sort(
    (a, b) => Number(b.builtin) - Number(a.builtin) || a.position - b.position,
  );
  return ordered.map((s) => {
    const placed = tasks
      .filter((t) => where.get(t.id)?.section === s.id)
      .sort((a, b) => (where.get(a.id)?.position ?? 0) - (where.get(b.id)?.position ?? 0));
    const loose =
      s.id === "recent"
        ? tasks
            .filter((t) => !where.has(t.id))
            .sort(
              (a, b) =>
                (a.due_at ?? "9999").localeCompare(b.due_at ?? "9999") || a.number - b.number,
            )
        : [];
    return { id: s.id, name: s.name, builtin: s.builtin, tasks: [...placed, ...loose] };
  });
}

/** The section a task is in (Recently assigned without a placement). */
export function sectionOf(data: MySections, taskId: string): string {
  const known = new Set(data.sections.map((s) => s.id));
  const p = data.placements.find((x) => x.task_id === taskId);
  return p && known.has(p.section) ? p.section : "recent";
}

/**
 * The server's `position` for a drop at `toIndex` of a section's shown rows (the dragged one
 * already left): placed rows count; loose rows (no placement, shown after them) do not.
 */
export function placePosition(
  data: MySections,
  rows: readonly { id: string }[],
  taskId: string,
  toIndex: number,
): number {
  const known = new Set(data.sections.map((s) => s.id));
  const placed = new Set(data.placements.filter((p) => known.has(p.section)).map((p) => p.task_id));
  return rows
    .filter((r) => r.id !== taskId)
    .slice(0, toIndex)
    .filter((r) => placed.has(r.id)).length;
}

/** The sections' data after a move (the server's rule: the section's places are renumbered). */
export function withPlacement(
  data: MySections,
  taskId: string,
  section: string,
  position?: number,
): MySections {
  const rest = data.placements.filter((p) => p.task_id !== taskId);
  const target = rest.filter((p) => p.section === section).sort((a, b) => a.position - b.position);
  const at = Math.max(0, Math.min(target.length, position ?? target.length));
  const moved: Placement = { task_id: taskId, section, position: at };
  target.splice(at, 0, moved);
  return {
    ...data,
    placements: [
      ...rest.filter((p) => p.section !== section),
      ...target.map((p, i) => ({ ...p, position: i })),
    ],
  };
}
